import { Link } from 'react-router-dom'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { PolicyPage, PolicySection } from './PolicyPage'

export const TermsOfService = () => {
  const { t } = useLocalization()

  return (
    <PolicyPage title={t('Terms of service', 'Allmänna villkor')}>
      <PolicySection title={t('1. Who we are', '1. Vilka vi är')}>
        <p>
          {t(
            'These terms are between you and Klayder AB, Svandammsvägen 36, 126 34 Hägersten, Sweden (org. no. 559433-8104, VAT SE559433810401). “Klayder”, “we”, and “us” include the Just Merch platform at app.justmerch.co and klayder.com.',
            'Dessa villkor gäller mellan dig och Klayder AB, Svandammsvägen 36, 126 34 Hägersten, Sverige (org.nr 559433-8104, VAT SE559433810401). ”Klayder”, ”vi” och ”oss” inkluderar Just Merch-plattformen på app.justmerch.co och klayder.com.'
          )}
        </p>
        <p>
          {t(
            'Questions: carmelo@klayder.com.',
            'Frågor: carmelo@klayder.com.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('2. The agreement', '2. Avtalet')}>
        <p>
          {t(
            'By creating an account, using the platform, or approving a quote, you agree to these terms and our ',
            'Genom att skapa ett konto, använda plattformen eller godkänna en offert godkänner du dessa villkor och vår '
          )}
          <Link to={paths.privacyPolicy}>
            {t('privacy policy', 'integritetspolicy')}
          </Link>
          {t(
            '. If you use Klayder for a company, you confirm you are authorised to bind that company.',
            '. Om du använder Klayder för ett företag bekräftar du att du har rätt att binda företaget.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('3. Business customers only', '3. Endast företagskunder')}
      >
        <p>
          {t(
            'Klayder is a B2B service. Accounts require a work email. We do not sell to consumers, and Swedish consumer-purchase rules do not apply.',
            'Klayder är en B2B-tjänst. Konton kräver en arbetsmejl. Vi säljer inte till konsumenter, och konsumentköplagen gäller inte.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('4. The platform', '4. Plattformen')}>
        <p>
          {t(
            'Klayder is a full-service merch platform. You can browse the catalog, build quotes, chat with our team, and optionally use Sara, our assistant, for search, mockups, and advice. A Klayder person always reviews and handles the order — quote release, proofs, production, and delivery.',
            'Klayder är en fullserviceplattform för merch. Du kan bläddra i katalogen, bygga offerter, chatta med vårt team och valfritt använda Sara, vår assistent, för sök, mockups och råd. En person på Klayder granskar och hanterar alltid ordern — offertsläpp, korrekturer, produktion och leverans.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('5. Accounts', '5. Konton')}>
        <p>
          {t(
            'You must keep login details confidential and use accurate company information. We may suspend access for misuse, unpaid invoices, or security risk. You may sign in with a magic link or a Google or Microsoft work account.',
            'Du ska hålla inloggningsuppgifter konfidentiella och använda korrekta företagsuppgifter. Vi kan stänga av åtkomst vid missbruk, obetalda fakturor eller säkerhetsrisk. Du kan logga in med magisk länk eller ett Google- eller Microsoft-jobbkonto.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('6. Quotes, approval, and orders', '6. Offerter, godkännande och ordrar')}
      >
        <p>
          {t(
            'Catalog prices and draft quotes are invitations to treat, not binding offers. A quote becomes ready for you to approve only after Klayder staff have reviewed and released it. Quotes are valid for 30 days unless the quote says otherwise.',
            'Katalogpriser och utkast till offerter är inte bindande anbud. En offert blir redo för ditt godkännande först när Klayder har granskat och släppt den. Offerter gäller i 30 dagar om inte offerten säger något annat.'
          )}
        </p>
        <p>
          {t(
            'Approving a quote is a binding order for the products, decorations, quantities, and prices on that quote. We then prepare factory proofs. Production starts after proofs are signed off, unless we agree otherwise in writing.',
            'Att godkänna en offert är en bindande order för produkterna, dekorationerna, kvantiteterna och priserna på den offerten. Därefter tar vi fram korrekturer. Produktion startar när korrekturerna är godkända, om vi inte avtalar något annat skriftligen.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('7. Artwork and intellectual property', '7. Original och immateriella rättigheter')}>
        <p>
          {t(
            'You keep the rights to logos, artwork, and brand assets you upload. You grant Klayder a licence to use them to produce mockups, proofs, and the order. You warrant that you have the right to use that artwork and that production will not infringe anyone else’s rights. You indemnify Klayder against claims arising from artwork you provide.',
            'Du behåller rättigheterna till logotyper, original och varumärkestillgångar som du laddar upp. Du ger Klayder en licens att använda dem för mockups, korrekturer och ordern. Du garanterar att du har rätt att använda originalen och att produktionen inte gör intrång i någon annans rättigheter. Du håller Klayder skadeslös för krav som följer av original du tillhandahåller.'
          )}
        </p>
        <p>
          {t(
            'Klayder and our suppliers keep the rights to the platform, catalog photography, mockup tooling, and our own methods. Mockups are previews, not a guarantee of final print placement or colour.',
            'Klayder och våra leverantörer behåller rättigheterna till plattformen, katalogfoton, mockup-verktyg och våra egna metoder. Mockups är förhandsvisningar, inte en garanti för slutlig placering eller färg.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('8. Pricing, invoices, and payment', '8. Priser, fakturor och betalning')}
      >
        <p>
          {t(
            'Prices on a released quote exclude VAT unless stated otherwise. Swedish VAT is added where applicable (currently 25%). Payment terms are 30 days net unless the quote or invoice says otherwise. Invoices are issued by Klayder AB.',
            'Priser på en släppt offert är exklusive moms om inget annat anges. Svensk moms läggs på där det är tillämpligt (för närvarande 25 %). Betalningsvillkor är 30 dagar netto om inte offerten eller fakturan säger något annat. Fakturor utfärdas av Klayder AB.'
          )}
        </p>
        <p>
          {t(
            'Late payment may accrue interest according to Swedish law, and we may pause production or delivery until the invoice is paid.',
            'Vid sen betalning kan ränta tas ut enligt svensk lag, och vi kan pausa produktion eller leverans tills fakturan är betald.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('9. Production and delivery', '9. Produktion och leverans')}>
        <p>
          {t(
            'Lead times are estimates. Custom merch depends on decoration, factory capacity, and freight. Risk in the goods passes when we hand them to the carrier, unless we agree otherwise. You must inspect deliveries and notify us of visible defects without delay.',
            'Leveranstider är uppskattningar. Specialmerch beror på dekoration, fabrikskapacitet och frakt. Risken för varorna går över när vi lämnar dem till transportören, om vi inte avtalar något annat. Du ska kontrollera leveranser och meddela oss om synliga fel utan dröjsmål.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('10. Samples', '10. Prover')}>
        <p>
          {t(
            'We may offer samples. Samples show quality and colour as closely as production allows; small variation in fabric, print, and embroidery is normal in this industry.',
            'Vi kan erbjuda prover. Prover visar kvalitet och färg så nära produktionen som möjligt; små variationer i tyg, tryck och brodyr är normala i den här branschen.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('11. Cancellation and complaints', '11. Avbeställning och reklamation')}
      >
        <p>
          {t(
            'Custom-branded merchandise is made to order. After you approve the quote, you cannot cancel or return the order because you changed your mind. Before production starts we may agree to change or cancel in writing; costs already incurred may still be charged.',
            'Märkesmerch tillverkas på beställning. Efter att du godkänt offerten kan du inte avbeställa eller returnera ordern för att du ångrat dig. Innan produktion startar kan vi skriftligen komma överens om ändring eller avbeställning; kostnader som redan uppstått kan ändå debiteras.'
          )}
        </p>
        <p>
          {t(
            'If goods are defective or do not match the approved proofs in a material way, contact us at carmelo@klayder.com. We will repair, replace, or credit as appropriate. Claims must be made within a reasonable time after you noticed, or should have noticed, the issue.',
            'Om varor är felaktiga eller i väsentlig mån inte stämmer med godkända korrekturer, kontakta oss på carmelo@klayder.com. Vi åtgärdar, ersätter eller krediterar efter vad som är skäligt. Reklamation ska göras inom skälig tid efter att du märkte, eller borde ha märkt, felet.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('12. Sara', '12. Sara')}>
        <p>
          {t(
            'Sara is optional. Suggestions, search results, and mockups can be incomplete or wrong. They do not replace your review or Klayder’s human review. Do not share information in chat that you would not want processed to fulfil the order.',
            'Sara är valfri. Förslag, sökresultat och mockups kan vara ofullständiga eller felaktiga. De ersätter inte din granskning eller Klayders mänskliga granskning. Dela inte information i chatten som du inte vill att vi behandlar för att genomföra ordern.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('13. Acceptable use', '13. Tillåten användning')}>
        <p>
          {t(
            'You may not misuse the platform, attempt to access other customers’ data, reverse engineer the service, scrape the catalog in an abusive way, or upload unlawful or infringing content.',
            'Du får inte missbruka plattformen, försöka komma åt andra kunders data, reverse-engineera tjänsten, skrapa katalogen på ett missbrukande sätt eller ladda upp olagligt eller rättighetskränkande innehåll.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('14. Liability', '14. Ansvar')}>
        <p>
          {t(
            'Klayder is not liable for indirect or consequential loss, including lost profit, lost data, or delay, except in case of intent or gross negligence. Our total liability for an order is limited to the amount you paid (or should pay) for that order, excluding VAT.',
            'Klayder ansvarar inte för indirekt skada, inklusive utebliven vinst, förlorad data eller försening, utom vid uppsåt eller grov vårdslöshet. Vårt sammanlagda ansvar för en order är begränsat till beloppet du betalat (eller ska betala) för den ordern, exklusive moms.'
          )}
        </p>
        <p>
          {t(
            'Nothing in these terms limits liability that cannot be limited under Swedish law.',
            'Inget i dessa villkor begränsar ansvar som inte får begränsas enligt svensk lag.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('15. Changes', '15. Ändringar')}>
        <p>
          {t(
            'We may update these terms. The version on this page applies to new use of the platform and to quotes approved after the “Last updated” date. Material changes to an existing order require agreement between us.',
            'Vi kan uppdatera dessa villkor. Versionen på den här sidan gäller ny användning av plattformen och offerter som godkänns efter datumet ”Senast uppdaterad”. Väsentliga ändringar av en befintlig order kräver överenskommelse mellan oss.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('16. Governing law', '16. Tillämplig lag')}>
        <p>
          {t(
            'These terms are governed by Swedish law. Disputes shall be settled by the courts of Sweden, with Stockholm District Court as the court of first instance.',
            'Dessa villkor styrs av svensk lag. Tvister ska avgöras av svensk domstol, med Stockholms tingsrätt som första instans.'
          )}
        </p>
      </PolicySection>
    </PolicyPage>
  )
}
